import { z } from 'zod';
import { idSchema, patchOf, publicAssetSchema } from './common';

const localizedText = z.record(z.string(), z.string());

export const barcodeSchema = z
  .string()
  .trim()
  .regex(/^[0-9A-Za-z\-.$/+% ]{4,32}$/, 'Use 4–32 letters, digits, spaces or - . $ / + %');

export const createMenuCategorySchema = z.object({
  name: localizedText,
  sortOrder: z.number().int().default(0),
  visible: z.boolean().default(true),
});
export type CreateMenuCategoryInput = z.infer<typeof createMenuCategorySchema>;
export const updateMenuCategorySchema = patchOf(createMenuCategorySchema);
export type UpdateMenuCategoryInput = z.infer<typeof updateMenuCategorySchema>;

export const createMenuItemSchema = z.object({
  categoryId: idSchema,
  name: localizedText,
  description: localizedText.default({}),
  priceMinor: z.number().int().min(0).max(100_000_000).default(0),
  photoAssetId: idSchema.nullable().default(null),
  sortOrder: z.number().int().default(0),
  visible: z.boolean().default(true),
  sellable: z.boolean().default(true).describe('Rung up at the bar; independent of whether the website shows it'),
  trackStock: z.boolean().default(false).describe('The bar counts it per branch'),
  barcode: barcodeSchema.nullable().default(null).describe("The product's barcode (EAN-13, UPC or any Code 128 text); one per gym"),
  leadTimeDays: z.number().int().min(1).max(365).nullable().default(null).describe('Days from ordering to the delivery arriving; the bar suggests an order only for items that have one'),
  packSize: z.number().int().min(1).max(10_000).nullable().default(null).describe('Units per case the supplier sells; a suggested order is rounded up to it'),
});
export type CreateMenuItemInput = z.infer<typeof createMenuItemSchema>;
export const updateMenuItemSchema = patchOf(createMenuItemSchema);
export type UpdateMenuItemInput = z.infer<typeof updateMenuItemSchema>;

export const menuItemSchema = z.object({
  id: idSchema,
  categoryId: idSchema,
  name: localizedText,
  description: localizedText,
  priceMinor: z.number().int(),
  photoAssetId: idSchema.nullable(),
  sortOrder: z.number().int(),
  visible: z.boolean(),
  sellable: z.boolean(),
  trackStock: z.boolean(),
  barcode: z.string().nullable(),
  leadTimeDays: z.number().int().nullable(),
  packSize: z.number().int().nullable(),
});
export type MenuItemDto = z.infer<typeof menuItemSchema>;

export const menuCategorySchema = z.object({
  id: idSchema,
  name: localizedText,
  sortOrder: z.number().int(),
  visible: z.boolean(),
  items: z.array(menuItemSchema),
});
export type MenuCategoryDto = z.infer<typeof menuCategorySchema>;

/** What the public site gets: only what is visible, and the item photos already resolved to URLs. */
export const publicMenuItemSchema = menuItemSchema.omit({ visible: true, sellable: true, trackStock: true, photoAssetId: true, barcode: true, leadTimeDays: true, packSize: true }).extend({
  photo: publicAssetSchema.nullable(),
});
export const publicMenuCategorySchema = z.object({
  id: idSchema,
  name: localizedText,
  sortOrder: z.number().int(),
  items: z.array(publicMenuItemSchema),
});
export type PublicMenuCategoryDto = z.infer<typeof publicMenuCategorySchema>;
